// src/utils/calcModifyRoomTypePreview.ts

// ---------------------------------------------
// Helpers for Modify Room Type page (NO UI logic)
// ---------------------------------------------

import { calcBookingBreakdown, BookingBreakdown, BreakdownLine } from "utils/calcBookingBreakdown";

type TripLike = any;

// Minimal shape we need from RoomType
export type RoomTypeLike = {
  roomTypeId: string;
  name?: string;
  bed?: string;
  hasWindows?: boolean;
  capacity?: number;
  pricePerNight?: number;
};

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

const pickRoomBookingsToCount = (booking: any) => {
  const notDeleted = (booking?.roomBookings || []).filter((rb: any) => !rb?.isDeleted);
  const current = notDeleted.filter((rb: any) => rb?.isCurrent);

  if (current.length > 0) return current;
  return notDeleted.length > 0 ? [notDeleted[notDeleted.length - 1]] : [];
};

const applyRoomTypeToBooking = (booking: any, newRoomType: RoomTypeLike) => {
  const rbs = pickRoomBookingsToCount(booking);

  for (const rb of rbs) {
    // Update price used by calcBookingBreakdown (totalPrice fallback → pricePerNight * nights)
    rb.pricePerNight = Number(newRoomType?.pricePerNight ?? rb?.pricePerNight ?? 0);

    // Force breakdown to use pricePerNight*nights if old totalPrice is present
    // (otherwise preview can be wrong if backend stored stale totalPrice)
    rb.totalPrice = 0;

    // Inject new roomType on the room object (so titles are correct)
    if (!rb.room) rb.room = {};
    rb.room.roomType = {
      ...(rb.room.roomType || {}),
      ...newRoomType,
      // Ensure id is consistent
      roomTypeId: newRoomType.roomTypeId,
    };
  }
};

export type ModifyRoomTypePreviewInput = {
  trip: TripLike | null | undefined;
  bookingId: string;
  applyToAll: boolean;
  finalSelectedRoomType: RoomTypeLike | null;
};

export type ModifyRoomTypePreviewLine = BreakdownLine & {
  // UI helper: highlight only room lines of affected bookings
  isModified?: boolean;
};

export type ModifyRoomTypePreviewResult = {
  original: BookingBreakdown;
  preview: BookingBreakdown;

  totalDiff: number;

  modifiedBookingIds: string[];
  previewLines: ModifyRoomTypePreviewLine[];
};

export const calcModifyRoomTypePreview = (
  input: ModifyRoomTypePreviewInput
): ModifyRoomTypePreviewResult | null => {
  const { trip, bookingId, applyToAll, finalSelectedRoomType } = input;

  if (!trip) return null;

  const original = calcBookingBreakdown(trip);

  // If nothing selected → no preview change
  if (!finalSelectedRoomType?.roomTypeId) {
    return {
      original,
      preview: original,
      totalDiff: 0,
      modifiedBookingIds: [],
      previewLines: original.lines,
    };
  }

  const modifiedTrip = clone(trip);

  const bookings: any[] = modifiedTrip?.bookings || [];
  const modifiedBookingIds: string[] = [];

  for (const b of bookings) {
    const shouldChange = applyToAll ? true : b?.bookingId === bookingId;
    if (!shouldChange) continue;

    applyRoomTypeToBooking(b, finalSelectedRoomType);
    if (b?.bookingId) modifiedBookingIds.push(b.bookingId);
  }

  const preview = calcBookingBreakdown(modifiedTrip);

  // Mark affected ROOM lines (booking_<id>) as modified for UI
  const modifiedSet = new Set(modifiedBookingIds);

  const previewLines: ModifyRoomTypePreviewLine[] = preview.lines.map((l) => {
    const key = String(l.key || "");
    const isBookingLine = key.startsWith("booking_");
    const id = isBookingLine ? key.replace("booking_", "") : "";

    return {
      ...l,
      isModified: isBookingLine && modifiedSet.has(id),
    };
  });

  return {
    original,
    preview,
    totalDiff: Number((preview.total - original.total).toFixed(2)),
    modifiedBookingIds,
    previewLines,
  };
};